import { atom } from 'infrastructure/state/jotai.wrapper'
import { nodeAltTextData } from './utility'
import { leafAltTextData, UrlData } from '../utility/nodeIssues'
import { createIdMap } from '../../version_mapping/utility'
import { INode } from 'shared/types/tree'
import { ExportOptions, GeneratedAsset } from './types'
import { allNodePathsById } from '../utility/utility'
import { getNodeUrl } from '../../utility/TreeNavigation'

export const altTextTreeAtom = atom<{ tree?: INode; resetTree?: () => void }>(
  {},
)

export const altTextSourceDataAtom = atom<Record<string, GeneratedAsset>>({})

export const exportOptionsAtom = atom<ExportOptions>({
  excludeWithoutAltText: false,
})

export const altTextIdMapAtom = atom((get) => {
  const { tree } = get(altTextTreeAtom)
  if (!tree) return {}
  return createIdMap(tree)
})

export const byUrlDataAtom = atom((get) => {
  const { tree } = get(altTextTreeAtom)
  if (!tree) return {}
  const byUrl = nodeAltTextData(tree)
  return Object.entries(leafAltTextData(tree)).reduce((acc, [url, data]) => {
    if (url in byUrl) {
      acc[url].leaves = data.leaves
    } else {
      acc[url] = data
    }
    return acc
  }, byUrl)
})

export const urlSortAtom = atom((get) => {
  const allUrlData = get(byUrlDataAtom)
  return (url1: string, url2: string) => {
    const { nodes: nodes1, leaves: leaves1 } = allUrlData[url1]
    const { nodes: nodes2, leaves: leaves2 } = allUrlData[url2]
    if (nodes1.length > 0 || nodes2.length > 0) {
      if (nodes1.length === nodes2.length) return url1.localeCompare(url2)
      return nodes2.length - nodes1.length
    }
    if (leaves1.length === leaves2.length) return url1.localeCompare(url2)
    return leaves2.length - leaves1.length
  }
})

export const sortedTreeUrlsAtom = atom((get) => {
  const allUrlData = get(byUrlDataAtom)
  return Object.values(allUrlData).reduce<{
    withAltText: string[]
    withoutAltText: string[]
  }>(
    ({ withAltText, withoutAltText }, { url, altText }) => {
      if (altText) {
        withAltText.push(url)
      } else {
        withoutAltText.push(url)
      }
      return { withAltText, withoutAltText }
    },
    { withAltText: [], withoutAltText: [] },
  )
})

export const urlsWithAvailableAltText = atom<string[]>((get) => {
  const { withoutAltText } = get(sortedTreeUrlsAtom)
  const altTextSource = get(altTextSourceDataAtom)
  return withoutAltText.reduce<string[]>((acc, existing) => {
    if (existing in altTextSource) {
      acc.push(existing)
    }
    return acc
  }, [])
})

const filteredDataAtom = atom((get) => {
  const filterData = get(exportOptionsAtom)
  const theData = get(byUrlDataAtom)

  return Object.values(theData).reduce<UrlData[]>((acc, data) => {
    if (filterData.excludeWithoutAltText && !data.altText) return acc
    return [...acc, data]
  }, [])
})

const nodeLeafDataAtom = atom((get) => {
  const filteredData = get(filteredDataAtom)

  return filteredData.reduce<{
    nodes: Record<string, { altText: string; url: string }>
    leaves: Record<string, { altText: string; url: string; parentId: string }>
  }>(
    ({ nodes, leaves }, { altText, url, ...rest }) => {
      return {
        nodes: {
          ...nodes,
          ...rest.nodes.reduce(
            (acc, n) => ({ ...acc, [n.id]: { altText, url } }),
            {},
          ),
        },
        leaves: {
          ...leaves,
          ...(rest.leaves ?? []).reduce(
            (acc, n) => ({
              ...acc,
              [n.id]: { altText, url, parentId: n.parent_id },
            }),
            {},
          ),
        },
      }
    },
    {
      nodes: {},
      leaves: {},
    },
  )
})

const nodePathsAtom = atom((get) => {
  const sortedData = get(nodeLeafDataAtom)
  const idMap = get(altTextIdMapAtom)

  const nodes = [
    ...Object.keys(sortedData.nodes).map((nid) => idMap[nid].node),
    ...Object.values(sortedData.leaves).map(
      ({ parentId }) => idMap[parentId].node,
    ),
  ]
  return allNodePathsById(nodes, idMap)
})

export const exportDataAtom = atom((get) => {
  const sortedData = get(nodeLeafDataAtom)
  const idMap = get(altTextIdMapAtom)
  const { paths, maxPath } = get(nodePathsAtom)

  const getPathData = (nodeId: string) => {
    const node = idMap[nodeId].node
    return [
      getNodeUrl(node, idMap),
      ...(paths[nodeId]?.map(({ nid }) => idMap[nid].node.title) ?? []),
    ]
  }

  const data = [
    ...Object.entries(sortedData.nodes).reduce<string[][]>(
      (acc, [nid, { url, altText }]) => {
        const node = idMap[nid].node
        return [...acc, [url, altText, 'node', node.type, ...getPathData(nid)]]
      },
      [],
    ),
    ...Object.entries(sortedData.leaves).reduce<string[][]>(
      (acc, [lid, { url, altText, parentId }]) => {
        const parent = idMap[parentId].node
        const leaf = parent.leaves!.find(({ id }) => lid === id)!
        return [
          ...acc,
          [url, altText, 'leaf', leaf.type ?? '', ...getPathData(parentId)],
        ]
      },
      [],
    ),
  ]
  return {
    headers: [
      'image url',
      'altText',
      'nodeOrLeaf',
      'type',
      'Kiddom URL',
      ...Array.from({ length: maxPath }).map(
        (_, index) => `Level ${index + 1}`,
      ),
    ],
    data,
  }
})
